import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User, Organization } from '../types';
import { permissionService } from './permissionService';
import { resolveDomainTenant } from '../utils/domainTenant';

export interface AuthUser extends FirebaseUser {
  profile?: User;
}

/**
 * Create a new organization for a user
 * @private
 */
async function createOrganizationForNewUser(
  userId: string,
  userName: string,
  userEmail: string,
  orgName?: string
): Promise<string> {
  try {
    // Generate organization ID
    const orgRef = doc(collection(db, 'organizations'));
    const orgId = orgRef.id;
    
    // Determine organization name
    const defaultOrgName = orgName || `${userName}'s Organization`;
    
    // Create organization document
    const organization: Partial<Organization> = {
      name: defaultOrgName,
      createdBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
      subscription: {
        plan: 'free',
        status: 'active',
        seats: 5
      }
    };
    
    await setDoc(orgRef, {
      ...organization,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log(`✅ Created organization ${orgId} for user ${userId}`);
    
    // Initialize system roles for this organization
    try {
      await permissionService.initializeSystemRoles(orgId, userId);
      console.log(`✅ Initialized system roles for organization ${orgId}`);
    } catch (roleError) {
      console.error('⚠️ Failed to initialize system roles:', roleError);
      // Don't fail registration if role initialization fails
    }
    
    // Create organization settings
    try {
      await setDoc(doc(db, 'settings', orgId), {
        orgId,
        industries: [
          'Technology', 'Healthcare', 'Financial Services', 'Manufacturing', 
          'Retail & E-Commerce', 'Education', 'Real Estate', 'Professional Services',
          'Media & Entertainment', 'Transportation & Logistics', 'Energy & Utilities',
          'Agriculture', 'Non-Profit', 'Government', 'Telecommunications', 
          'Hospitality', 'Construction', 'Pharmaceutical', 'Automotive', 'Other'
        ],
        contactStages: ['untouched', 'contacted', 'interested', 'qualified', 'converted'],
        opportunityStages: ['prospecting', 'qualification', 'proposal', 'negotiation', 'closing', 'won', 'lost'],
        updatedAt: serverTimestamp(),
        updatedBy: userId
      });
    } catch (settingsError) {
      console.error('⚠️ Failed to create organization settings:', settingsError);
    }
    
    return orgId;
  } catch (error) {
    console.error('Error creating organization for new user:', error);
    throw error;
  }
}

export const authService = {
  // Sign up new user
  async signUp(
    email: string, 
    password: string, 
    name: string,
    options?: {
      orgId?: string;      // Join existing organization
      orgName?: string;    // Custom organization name
    }
  ): Promise<AuthUser> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update user profile
      await updateProfile(user, { displayName: name });
      
      // Determine organization ID
      let orgId: string;
      let userRole: 'admin' | 'manager' | 'sales_rep' = 'sales_rep'; // Always lowest rank for new users
      
      if (options?.orgId) {
        // User is joining an existing organization
        orgId = options.orgId;
        userRole = 'sales_rep'; // Default role for new members
        console.log(`User ${user.uid} joining existing organization: ${orgId}`);
      } else {
        // Create new organization for user
        orgId = await createOrganizationForNewUser(user.uid, name, user.email!, options?.orgName);
        userRole = 'sales_rep'; // New users always get lowest rank (sales_rep)
        console.log(`Created new organization ${orgId} for user ${user.uid}`);
      }
      
      // Create user profile in Firestore
      const userProfile: User = {
        id: user.uid,
        name,
        email: user.email!,
        role: userRole,
        specialties: [],
        currentLeadCount: 0,
        orgId: orgId // ✅ Set proper organization ID
      };
      
      await setDoc(doc(db, 'users', user.uid), userProfile);
      
      console.log(`✅ User ${user.uid} successfully registered with orgId: ${orgId}`);
      
      return { ...user, profile: userProfile };
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  },

  // Sign in existing user
  async signIn(email: string, password: string): Promise<AuthUser> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Get user profile from Firestore
      const profileDoc = await getDoc(doc(db, 'users', user.uid));
      const profile = profileDoc.exists() ? profileDoc.data() as User : undefined;
      
      return { ...user, profile };
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  },

  // Sign out
  async signOut(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  },

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          // Get user profile from Firestore
          const profileDoc = await getDoc(doc(db, 'users', user.uid));
          const profile = profileDoc.exists() ? profileDoc.data() as User : undefined;
          callback({ ...user, profile });
        } else {
          callback(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        // If there's an error getting profile, still return user without profile
        if (user) {
          callback({ ...user, profile: undefined });
        } else {
          callback(null);
        }
      }
    });
  },

  // Get current user
  getCurrentUser(): AuthUser | null {
    return auth.currentUser as AuthUser | null;
  },

  // Reload user profile from Firestore
  async reloadUserProfile(): Promise<User | null> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return null;
      
      const profileDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!profileDoc.exists()) return null;
      
      const profile = profileDoc.data() as User;
      console.log(`✅ Reloaded user profile for ${currentUser.uid}`);
      return profile;
    } catch (error) {
      console.error('Error reloading user profile:', error);
      return null;
    }
  },

  // Google Sign-In
  async signInWithGoogle(): Promise<AuthUser> {
    try {
      const provider = new GoogleAuthProvider();
      // Optional: restrict to the provided web client ID via custom parameters
      provider.setCustomParameters({
        client_id: '1075250029985-dm04i731q26mlssi3kl65sks6lfngfsb.apps.googleusercontent.com'
      } as any);

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Ensure user profile exists
      const profileRef = doc(db, 'users', user.uid);
      const existing = await getDoc(profileRef);
      if (!existing.exists()) {
        const userName = user.displayName || user.email || 'User';

        // Firmen-Domain (z. B. @echomotion.de) → bestehender Firmen-Tenant statt neuer Organisation
        const profile = getAdditionalUserInfo(result)?.profile as { hd?: string } | null | undefined;
        const tenant = resolveDomainTenant({
          email: user.email,
          emailVerified: user.emailVerified,
          hostedDomain: profile?.hd,
        });
        const orgId = tenant
          ? tenant.orgId
          : await createOrganizationForNewUser(user.uid, userName, user.email || '');

        const userProfile: User = {
          id: user.uid,
          name: userName,
          email: user.email || '',
          role: 'sales_rep', // New users always get lowest rank (sales_rep)
          specialties: [],
          currentLeadCount: 0,
          orgId: orgId, // ✅ Set proper organization ID
          ...(tenant && {
            organizations: [{
              orgId: tenant.orgId,
              orgName: tenant.orgName,
              role: 'sales_rep',
              joinedAt: new Date(),
              status: 'active',
            }],
            lastActiveOrg: tenant.orgId,
          }),
          avatar: user.photoURL || undefined // Save Google profile photo
        };
        await setDoc(profileRef, userProfile);
        console.log(`✅ Google user ${user.uid} successfully registered with orgId: ${orgId}`);
        return { ...user, profile: userProfile } as AuthUser;
      } else {
        // Update existing profile with Google photo if not set
        const profileData = existing.data() as User;
        if (!profileData.avatar && user.photoURL) {
          await setDoc(profileRef, { ...profileData, avatar: user.photoURL }, { merge: true });
          return { ...user, profile: { ...profileData, avatar: user.photoURL } } as AuthUser;
        }
        return { ...user, profile: profileData } as AuthUser;
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  }
};